// Analytics sanitization utilities to prevent accidental PII / secrets leakage
// Centralized allow / block logic so any future sinks (PostHog, Langfuse, etc.) reuse consistent filtering.

export interface SanitizedRecord {
  [key: string]: unknown;
}

// Keys explicitly allowed (whitelist) for user traits.
// Anything else is dropped unless it passes generic safety rules.
const ALLOWED_TRAIT_KEYS = new Set([
  "role",
  "profileNickname",
  "isVerified",
  "subscriptionExpiry",
  // Intentionally exclude raw telegram_id / username from emission to analytics
]);

// Keys that, if matched (case-insensitive substring), are always removed.
const BLOCKED_KEY_SUBSTRINGS = [
  "token",
  "secret",
  "password",
  "pass",
  "key",
  "auth",
  "cookie",
  "session",
  "initdata",
];

// Max depth & size controls to avoid sending large nested payloads.
const MAX_DEPTH = 2;
const MAX_STRING_LENGTH = 200; // prevent huge blobs

function isBlockedKey(key: string): boolean {
  const lower = key.toLowerCase();
  return BLOCKED_KEY_SUBSTRINGS.some((substr) => lower.includes(substr));
}

function truncate(value: unknown): unknown {
  if (typeof value === "string") {
    if (value.length > MAX_STRING_LENGTH) {
      return value.slice(0, MAX_STRING_LENGTH) + "…";
    }
  }
  return value;
}

function shallowCloneSafe(
  obj: Record<string, unknown>,
  depth: number,
  predicate?: (k: string, v: unknown) => boolean,
): SanitizedRecord {
  const out: SanitizedRecord = {};
  for (const [k, v] of Object.entries(obj)) {
    if (predicate && !predicate(k, v)) continue;
    if (isBlockedKey(k)) continue;
    if (v === undefined) continue;
    if (v === null) {
      out[k] = null;
      continue;
    }
    if (typeof v === "function") continue;
    if (typeof v === "object") {
      if (depth >= MAX_DEPTH) continue; // drop deep structures
      if (Array.isArray(v)) {
        out[k] = v
          .slice(0, 10)
          .map((item) =>
            typeof item === "string"
              ? truncate(item)
              : typeof item === "number" || typeof item === "boolean"
                ? item
                : null,
          );
      } else {
        out[k] = shallowCloneSafe(v as Record<string, unknown>, depth + 1);
      }
      continue;
    }
    out[k] = truncate(v);
  }
  return out;
}

export function sanitizeTraits(
  traits?: Record<string, unknown>,
): SanitizedRecord | undefined {
  if (!traits) return undefined;
  // Only keep whitelisted keys.
  const filtered: Record<string, unknown> = {};
  for (const key of Object.keys(traits)) {
    if (!ALLOWED_TRAIT_KEYS.has(key)) continue; // skip anything not explicitly allowed
    const value = traits[key];
    if (value === undefined || value === null) continue;
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      filtered[key] = truncate(value);
    }
  }
  return Object.keys(filtered).length ? filtered : undefined;
}

export function sanitizeEventProps(
  props?: Record<string, unknown>,
): SanitizedRecord | undefined {
  if (!props) return undefined;
  return shallowCloneSafe(props, 0, (k) => !isBlockedKey(k));
}

export function normalizeEventName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_:.]/g, "");
}

// Narrow typing helper for Vite env without resorting to broad untyped casting
type ImportMetaEnvSubset = { VITE_ANALYTICS_DISABLE?: string };

export function shouldBlockAnalytics(): boolean {
  // Environment kill-switch (VITE_ANALYTICS_DISABLE=1) + optional runtime opt out
  const envDisabled =
    (import.meta as unknown as { env?: ImportMetaEnvSubset }).env
      ?.VITE_ANALYTICS_DISABLE === "1";
  let runtimeOptOut = false;
  try {
    runtimeOptOut =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("analytics_opt_out") === "1";
  } catch {
    /* storage unavailable */
  }
  return envDisabled || runtimeOptOut;
}
